import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dialog from '@cobliteam/ui/components/Dialog';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { DRIVER_FACE_PHOTO_MAX } from '@cobliteam/ui-business/components/drawers/DriverDrawer/constants';

interface FooterProps {
  page: number;
  totalPages: number;
  selectedCount: number;
  selectedPhotosCount: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
}

export const Footer = ({ page, totalPages, selectedCount, selectedPhotosCount, onPrevPage, onNextPage, onCancel, onConfirm, isSubmitting }: FooterProps) => {
  const { t } = useTranslation('drivers');
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const noGroupSelected = selectedCount === 0;
  const selectedOverMax = selectedPhotosCount > DRIVER_FACE_PHOTO_MAX;
  const confirmDisabled = noGroupSelected || selectedOverMax || isSubmitting;

  const getConfirmTooltip = (): string => {
    if (noGroupSelected) return t('face_id.selector.actions.link_tooltip_no_selection');
    if (selectedOverMax) return t('face_id.selector.actions.link_tooltip_max_reached', { max: DRIVER_FACE_PHOTO_MAX, selected: selectedPhotosCount });
    return '';
  };

  const confirmTooltip = getConfirmTooltip();

  return (
    <div className='flex items-center gap-2'>
      <Button
        type='button'
        variant='ghost'
        color='default'
        size='sm'
        aria-label={t('face_id.selector.pagination.prev')}
        icon={<Icon name='chevron-left' />}
        onClick={onPrevPage}
        disabled={!canPrev || isSubmitting}
      />
      <Typography variant='text-md-medium' color='secondary'>
        {page} / {totalPages}
      </Typography>
      <Button
        type='button'
        variant='ghost'
        color='default'
        size='sm'
        aria-label={t('face_id.selector.pagination.next')}
        icon={<Icon name='chevron-right' />}
        onClick={onNextPage}
        disabled={!canNext || isSubmitting}
      />
      <div className='flex-1' />
      <Dialog.Close asChild>
        <Button type='button' variant='ghost' disabled={isSubmitting} onClick={onCancel}>
          {t('face_id.selector.actions.cancel')}
        </Button>
      </Dialog.Close>
      <Tooltip content={confirmTooltip} enable={Boolean(confirmTooltip)}>
        <Button type='button' onClick={onConfirm} disabled={confirmDisabled} loading={isSubmitting}>
          {t('face_id.selector.actions.link')}
        </Button>
      </Tooltip>
    </div>
  );
};

export default Footer;
